import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import Section from "../components/marketing/Section";
import SectionHeading from "../components/marketing/SectionHeading";

/**
 * /privacy — written against a full audit of what the code actually stores,
 * not from a template. Templates describe data flows a product doesn't have
 * and omit the ones it does; the whole value of this page is that every
 * claim was checked against the schema and the Lambdas.
 *
 * If you change what's collected, retained, or shared, this page and
 * /security both have to change with it. Specific things that would make
 * this page wrong:
 *   - adding any analytics/tracking vendor (there are none — first-party
 *     CloudFront access logs only, no cookies set by us)
 *   - changing CloudWatch or the CloudFront log bucket's 30-day retention
 *   - storing diffs or file contents (today only filenames and line counts
 *     are kept; diffs are read in-memory and discarded)
 *   - adding a sub-processor beyond GitHub / AWS / Neon / Stripe
 *
 * Not lawyer-reviewed. Accurate to the system, which is the part templates
 * get wrong, but worth a professional read before it matters.
 */

const OPERATOR = "Meng Yan Hou";
const CONTACT = "support@anthurium.sh";
const PROVINCE = "British Columbia";
const LAST_UPDATED = "24 September 2026";

const ulClass = "mb-4 list-disc space-y-2 pl-5 marker:text-rf-text-muted";

const SECTIONS = [
  {
    id: "who-we-are",
    title: "1. Who we are",
    body: (
      <>
        <p className="mb-4">
          Anthurium is built and operated by {OPERATOR}, an individual based in{" "}
          {PROVINCE}, Canada. It is not an incorporated company. That means one
          person is accountable for how your information is handled, and you
          can reach them directly at{" "}
          <a href={`mailto:${CONTACT}`} className="text-rf-accent hover:underline">
            {CONTACT}
          </a>
          .
        </p>
        <p>
          This policy explains what Anthurium collects, why, who else sees it,
          how long it's kept, and what you can ask us to do with it.
        </p>
      </>
    ),
  },
  {
    id: "scope",
    title: "2. What this covers",
    body: (
      <p>
        The Anthurium web application and GitHub App. It does not
        cover GitHub itself — your GitHub account and repositories are governed
        by GitHub's own privacy policy — or any third-party site we link to.
      </p>
    ),
  },
  {
    id: "what-we-collect",
    title: "3. What we collect",
    body: (
      <>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">
            From GitHub, when you sign in.
          </span>{" "}
          Your GitHub user ID, username, display name, avatar URL, and your
          profile email address if you've made it public. We never receive your
          GitHub password.
        </p>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">
            From your pull requests.
          </span>{" "}
          For each pull request in a connected repository:
        </p>
        <ul className={ulClass}>
          <li>The repository name, pull request number, title, and URL</li>
          <li>The author's GitHub username</li>
          <li>
            The paths of changed files, and how many lines were added or removed
            in each
          </li>
          <li>
            Values we calculate from those: a complexity score, a priority, an
            estimated review time, and topic tags
          </li>
        </ul>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">
            Reviewer routing data.
          </span>{" "}
          Which pull requests were assigned to you and when, a short generated
          explanation of why, and a record of how often you've modified each
          file path. That record is what lets Anthurium route a review to
          whoever has actually worked on the code being changed.
        </p>
        {/*
          Added when repo_author_expertise shipped. The paragraph above
          describes this data for a signed-in user; that table holds the same
          counts keyed by GitHub username for commit authors who have never
          signed up, so a teammate joining later inherits their own history.
          Section 6 already discloses processing PR metadata for non-users —
          this is more granular, so it gets stated rather than folded in.
        */}
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">
            Commit history in connected repositories.
          </span>{" "}
          When a repository is connected we read up to six months of its recent
          commits and record, for each author's GitHub username, which file
          paths they changed and how often. We never read the contents of those
          commits. This is kept for everyone who has committed to the
          repository, including people who have never signed up for Anthurium
          — it's what lets a teammate who joins later start with their own
          history rather than nothing. See section 6 below.
        </p>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">
            What you give us directly.
          </span>{" "}
          A display name and a contact email address, if you set them on your
          profile.
        </p>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">Billing.</span>{" "}
          Payments are handled entirely by Stripe. We store only Stripe's
          customer and subscription identifiers and your subscription status.{" "}
          <span className="font-semibold text-rf-text-primary">
            We never see or store your card details.
          </span>{" "}
          If Stripe tells us the email you paid with and you haven't set a
          contact email, we save it as your contact email.
        </p>
        <p>
          <span className="font-semibold text-rf-text-primary">
            Technical logs.
          </span>{" "}
          Every request to our API is logged with your IP address, browser user
          agent, the request path, and the account and team identifiers
          involved. Our content delivery network separately logs each request's
          IP address, user agent, referring page, and requested URL. These logs
          exist to operate, secure, and debug the service.
        </p>
      </>
    ),
  },
  {
    id: "what-we-dont-collect",
    title: "4. What we don't collect",
    body: (
      <>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">
            We never store your source code.
          </span>{" "}
          To score a pull request, Anthurium asks GitHub for the list of files
          it changed. GitHub's response includes each file's diff — we read only
          the filename and the added and removed line counts from it, and the
          rest is discarded when the request finishes. No diff, file content,
          commit message, or branch name is written to our database.
        </p>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">
            We never store GitHub credentials.
          </span>{" "}
          The token issued when you sign in is used once, in that single
          request, to confirm who you are, and is never written anywhere.
          Background work uses installation tokens that GitHub issues on demand,
          which last one hour and are held only in memory.
        </p>
        <p>
          <span className="font-semibold text-rf-text-primary">
            No cookies, no trackers.
          </span>{" "}
          Anthurium sets no cookies of its own and uses no advertising or
          analytics trackers of any kind. Your session is kept in your browser's
          local storage and is removed when you sign out. We do not sell or
          share your information for advertising, ever.
        </p>
      </>
    ),
  },
  {
    id: "why",
    title: "5. Why we use it",
    body: (
      <>
        <p className="mb-4">We use the information above only to:</p>
        <ul className={ulClass}>
          <li>Sign you in and establish which team you belong to</li>
          <li>Score, prioritize, and route pull requests</li>
          <li>Produce team analytics for your own team</li>
          <li>Operate, secure, and debug the service</li>
          <li>Bill you, if you're on a paid plan</li>
          <li>Contact you about your account</li>
        </ul>
        <p>We do not use your data to train machine learning models.</p>
      </>
    ),
  },
  {
    id: "consent",
    title: "6. Consent — including for people who never signed up",
    body: (
      <>
        <p className="mb-4">
          You consent to this policy by installing the GitHub App and signing
          in. You can withdraw that consent at any time by deleting your account
          and uninstalling the App.
        </p>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">
            One thing we want to be direct about:
          </span>{" "}
          Anthurium records pull request metadata for{" "}
          <span className="font-semibold text-rf-text-primary">every author</span>{" "}
          in a connected repository — including people who have never signed up
          for Anthurium and may not know it's installed.
        </p>
        <p>
          If you install Anthurium on an organization's repositories, you are
          confirming that you have the authority to do so, and you are
          responsible for telling the people whose pull request activity will be
          processed. If you're one of those people and you'd like your data
          removed, email{" "}
          <a href={`mailto:${CONTACT}`} className="text-rf-accent hover:underline">
            {CONTACT}
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "7. Who else sees it",
    body: (
      <>
        <p className="mb-4">
          We use four service providers. We don't sell your information or share
          it with anyone else, except where the law requires it.
        </p>
        <ul className={ulClass}>
          <li>
            <span className="font-semibold text-rf-text-primary">GitHub</span>{" "}
            (United States) — identity and repository metadata
          </li>
          <li>
            <span className="font-semibold text-rf-text-primary">
              Amazon Web Services
            </span>{" "}
            (United States, us-east-1) — hosting, storage, content delivery,
            logs
          </li>
          <li>
            <span className="font-semibold text-rf-text-primary">Neon</span>{" "}
            (United States, us-east-2) — the database
          </li>
          <li>
            <span className="font-semibold text-rf-text-primary">Stripe</span> —
            payment processing
          </li>
        </ul>
        <p>
          <span className="font-semibold text-rf-text-primary">
            Your information is stored and processed in the United States.
          </span>{" "}
          While it's there it is subject to United States law, including lawful
          access requests by United States authorities, regardless of where you
          live.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "8. How long we keep it",
    body: (
      <>
        <ul className={ulClass}>
          <li>
            <span className="font-semibold text-rf-text-primary">
              Account, team, and pull request data
            </span>{" "}
            — for as long as your team uses Anthurium. Deleted when you delete
            your account.
          </li>
          <li>
            <span className="font-semibold text-rf-text-primary">
              Application and website logs
            </span>{" "}
            — 30 days, then deleted automatically.
          </li>
          <li>
            <span className="font-semibold text-rf-text-primary">
              Session tokens
            </span>{" "}
            — expire 24 hours after you sign in.
          </li>
          <li>
            <span className="font-semibold text-rf-text-primary">
              Billing records held by Stripe
            </span>{" "}
            — kept by Stripe under its own policy and applicable tax law. We
            don't delete your Stripe customer record when you delete your
            account, because invoice history is a legal record.
          </li>
          <li>
            <span className="font-semibold text-rf-text-primary">
              A record that your GitHub account has used a free trial
            </span>{" "}
            — 24 months. After that it stops counting against you immediately,
            and the record is deleted automatically.{" "}
            <span className="text-rf-text-primary">
              This one outlives your account on purpose.
            </span>{" "}
            Free trials are limited to one per GitHub organisation, and the only
            way to honour that is to remember which organisations have already
            had one. It's a single number — the numeric id GitHub assigns your
            account — with the date, and nothing else: no name, no email, no
            repositories. Deleting your Anthurium account does not remove it,
            so signing up again won't start a second trial.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "9. Your rights",
    body: (
      <>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">
            See what we hold.
          </span>{" "}
          Your profile is at <Link to="/profile" className="text-rf-accent hover:underline">/profile</Link>{" "}
          and your team's data is on your dashboard. There is currently no
          self-serve export — for a copy of everything we hold about you, email{" "}
          <a href={`mailto:${CONTACT}`} className="text-rf-accent hover:underline">
            {CONTACT}
          </a>{" "}
          and we'll respond within 30 days.
        </p>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">Correct it.</span>{" "}
          You can change your display name and contact email on your profile at
          any time. Fields that come from GitHub — your username, avatar, and
          public profile email — refresh from GitHub the next time you sign in.
        </p>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">Delete it.</span>{" "}
          You can delete your account yourself from your profile page. If you're
          the only member of your team, this schedules the team and all of its
          data for deletion too.
          {" "}
          <span className="font-semibold text-rf-text-primary">
            Deletion is scheduled, not immediate.
          </span>{" "}
          For 30 days nothing is removed and you can restore everything by
          signing back in with GitHub. After 30 days it is deleted permanently:
          your profile, your review assignments and your file-history data are
          removed, and your username is replaced on pull requests we've
          recorded.
        </p>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">
            Delete a team without deleting yourself.
          </span>{" "}
          If you're the last member of a team, you can delete just the team from
          the team page. That schedules everything belonging to it for deletion —
          pull request records, review assignments, repository connections, and
          analytics — and sets any active subscription to cancel at the end of
          the period you've paid for. You have 30 days to restore the team from
          your dashboard before any of it is removed. It also removes the
          Anthurium GitHub App from that organization, so our access to those
          repositories ends with the team. Your profile stays, and you can join
          or start another team. Two things outlive it: the
          file-history data that records which files you've worked on, which is
          tied to you rather than to the team, and the trial record described
          below.
        </p>
        <p className="mb-4">
          We'd rather be clear about the limits of that than overstate it:
        </p>
        <ul className={ulClass}>
          <li>
            If your team continues without you, pull requests you opened stay in
            its history, with the author shown as <code>[deleted]</code>. Titles
            you wrote remain.
          </li>
          <li>
            Your GitHub username may still appear inside repository names and
            pull request URLs, which we can't rewrite.
          </li>
          <li>
            Whenever a team is deleted — on its own, or because the last member
            deleted their account — we remove the Anthurium GitHub App from
            that organization as part of the same action. If that step fails we
            tell you, and you can remove the app yourself at any time from your
            organization's settings on GitHub. Deleting your account while
            others remain on the team leaves the team and the app in place, as
            you'd expect. If the app stays installed and you keep opening pull
            requests in a repository some other team still monitors, new
            metadata will be recorded again.
          </li>
          <li>
            If your GitHub organisation has used a free trial, we keep a record
            of that — the numeric id GitHub assigns your account, and the date —
            for 24 months. It survives deleting your account and deleting your
            team, because trials are limited to one per organisation and that's
            the only way to honour it. Deleting a team and starting a new one
            does not begin a second trial. See{" "}
            <span className="font-semibold">How long we keep things</span> above.
          </li>
        </ul>
        <p>
          <span className="font-semibold text-rf-text-primary">Complain.</span>{" "}
          Email us first and we'll try to put it right. You can also contact the
          Office of the Privacy Commissioner of Canada at{" "}
          <a
            href="https://www.priv.gc.ca"
            target="_blank"
            rel="noreferrer"
            className="text-rf-accent hover:underline"
          >
            priv.gc.ca
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "security",
    title: "10. Security",
    body: (
      <>
        {/*
          The GitHub App sentence was "holds read-only permissions and
          structurally cannot write to your repositories" until the review
          write-back shipped. That is a factual claim in a legal document, and
          gaining Pull requests: Write made the first half false — so it is
          restated as exactly what the App can and cannot do. The "cannot
          modify code" half remains structurally true: writing file contents
          needs Contents: Write, which the App does not hold.
        */}
        <p className="mb-4">
          Traffic is encrypted in transit with TLS. Database connections are
          encrypted. Secrets are held in AWS Secrets Manager rather than in
          code. The GitHub App can request a reviewer on a pull request; it
          cannot modify your code, push commits, or merge anything, because it
          does not hold the permissions that would allow it. No GitHub
          credentials are stored at rest. Session tokens are signed and expire
          after 24 hours.
        </p>
        <p>
          No method of transmission or storage is completely secure, and we
          won't pretend otherwise. If you believe you've found a security issue,
          please email{" "}
          <a href={`mailto:${CONTACT}`} className="text-rf-accent hover:underline">
            {CONTACT}
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "11. Children",
    body: (
      <p>
        Anthurium is a tool for software teams and isn't directed at anyone
        under 16. We don't knowingly collect information from children.
      </p>
    ),
  },
  {
    id: "changes",
    title: "12. Changes to this policy",
    body: (
      <p>
        If this policy changes, the "last updated" date above changes with it.
        For material changes we'll tell you in the app or by email to your
        contact address.
      </p>
    ),
  },
  {
    id: "contact",
    title: "13. Contact",
    body: (
      <p>
        {OPERATOR} — {PROVINCE}, Canada
        <br />
        <a href={`mailto:${CONTACT}`} className="text-rf-accent hover:underline">
          {CONTACT}
        </a>
      </p>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <div className="overflow-x-hidden bg-rf-bg-primary">
      <MarketingNav />

      <Section className="!pb-8">
        <SectionHeading
          eyebrow="Legal"
          title="Privacy Policy"
          lead={`Last updated ${LAST_UPDATED}`}
        />
      </Section>

      {SECTIONS.map((section, i) => (
        <Section
          key={section.id}
          id={section.id}
          variant={i % 2 === 1 ? "elevated" : "default"}
          className="!py-12 md:!py-16"
        >
          <div className="mx-auto max-w-[68ch]">
            <h2 className="mb-4 text-2xl font-bold tracking-tight text-rf-text-primary">
              {section.title}
            </h2>
            <div className="text-sm leading-relaxed text-rf-text-secondary">{section.body}</div>
          </div>
        </Section>
      ))}

      <MarketingFooter />
    </div>
  );
}
